import {
  DayCellInfo,
  EventDisplayInfo,
  SlotHeaderInfo
} from '@fullcalendar/react'
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { AddButton } from 'assets/icons'
import CircleContainer from 'components/containers/CircleContainer'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { useAppMode } from 'components/providers/AppModeProvider'
import {
  CALENDAR_VIEW,
  useCalendar
} from 'components/providers/CalendarProvider'
import { isToday } from 'date-fns'
import dayjs from 'dayjs'
import i18n from 'i18n'
import { t } from 'i18next'
import { getDefaultTimezone } from 'index'
import { createElement, useEffect, useState } from 'react'
import Database from 'services/db/Database'
import LocalStorage from 'services/LocalStorage'
import { EventDoc, GroupDoc, UserSettingsDoc } from 'types/api/changes'
import { CalendarRange } from 'types/components/calendar'
import { LocalStorageKey } from 'types/services/localStorage'
import { EventModelSnapshot } from 'types/snapshot/EventModelSnapshot'
import { isUserHaveGroupToAddEvents } from 'utils/api/groups'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { APP_COLOR } from 'utils/colors'
import { isMobileDevice } from 'utils/constants'
import {
  generateStartEndBasedOnTimeWithShift,
  isAMPM,
  StartEndEvent
} from 'utils/date'
import { isGuest } from 'utils/user'

import { EventBare as Event } from './Event'
import * as Styled from './styled'

const weekHeaderFontSize = isMobileDevice() ? 12 : 14

export interface DayHeaderContentProps {
  dayNameFormat?: string
  userSettings?: UserSettingsDoc
  groupId?: string
  groups?: GroupDoc[]
  handleAddEvent?: (startEnd: StartEndEvent) => void
  date: Date
  group?: GroupDoc
}

export function DayHeaderContent(props: DayHeaderContentProps) {
  const theme = useTheme()
  const textColor = theme.palette.text.primary
  const calendar = useCalendar()
  const dayNameTextColor = theme.palette.text.primary

  const dateForHeader = dayjs(props.date).toDate()
  const text = dayjs(dateForHeader).format('D')
  const today = isToday(dateForHeader)
  const appMode = useAppMode()

  const [_, setHaveActiveCalendar] = useState(false)
  const [showEventAddingNotPossible, onShowEventAddingNotPossible] =
    useState(false)

  const isLargeScreen = appMode.isCondensedWidth === false

  useEffect(() => {
    isUserHaveGroupToAddEvents().then((groupId) => {
      if (groupId) {
        setHaveActiveCalendar(true)
      } else {
        setHaveActiveCalendar(false)
      }
    })
  }, [props.groups])

  switch (calendar.view) {
    case CalendarRange.LISTDAY:
    case CalendarRange.LISTWEEK:
      return (
        <Box
          display={'flex'}
          flexDirection={'row'}
          height={'20px'}
          width={'100%'}
          gap={1}
          alignItems={'center'}
          position={'relative'}
        >
          {today && (
            <Box display={'flex'} flexDirection={'row'}>
              <Typography
                fontSize={isMobileDevice() ? '14px' : undefined}
                fontWeight={550}
                color={isGroupcalApp() ? 'white' : 'black'}
              >
                {t('today')}
              </Typography>
              <Typography
                fontSize={isMobileDevice() ? '14px' : undefined}
                className="day-name"
                color={isGroupcalApp() ? 'white' : 'black'}
              >
                {`${today ? ', ' : ''}`}
              </Typography>
            </Box>
          )}
          <Typography
            fontSize={isMobileDevice() ? '14px' : undefined}
            className="day-name"
            color={
              isGroupcalApp() && today
                ? 'white'
                : is24meApp() && today
                  ? 'black'
                  : theme.palette.text.primary
            }
          >
            {`${dayjs(dateForHeader).format(props.dayNameFormat ?? 'dddd')}, `}
          </Typography>
          <Typography
            className="day-number"
            fontSize={isMobileDevice() ? '14px' : undefined}
            color={
              isGroupcalApp() && today
                ? 'white'
                : is24meApp() && today
                  ? 'black'
                  : theme.palette.text.primary
            }
          >
            {dayjs(dateForHeader).format('MMM DD YYYY')}
          </Typography>
          <Box
            position={'absolute'}
            right={i18n.dir() === 'ltr' ? -6 : undefined}
            left={i18n.dir() === 'rtl' ? -6 : undefined}
          >
            {isGuest() ? (
              <></>
            ) : (
              <IconButton
                onClick={() => {
                  const now = dayjs()
                  const startEnd = generateStartEndBasedOnTimeWithShift(
                    dayjs(dateForHeader).set('hour', now.hour()).unix()
                  )
                  if (props.handleAddEvent) props.handleAddEvent(startEnd)
                }}
              >
                <AddButton width={'24px'} height={'24px'} color={'black'} />
              </IconButton>
            )}
          </Box>
          {showEventAddingNotPossible && (
            <ConfirmationDialog
              title={''}
              content={t('toAddEvents')}
              leftButtonHidden
              rightButton={`${t('ok')}`}
              rightButtonColor={APP_COLOR}
              handleRightButton={() => {
                onShowEventAddingNotPossible(false)
              }}
              open
              handleClose={() => {}}
            />
          )}
        </Box>
      )

    case CalendarRange.WEEK:
      return (
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <CircleContainer
            size={isMobileDevice() ? '24px' : undefined}
            color={
              isToday(dateForHeader) ? theme.palette.primary.main : undefined
            }
          >
            <div
              style={{
                width: '100%',
                fontWeight: '500',
                textAlign: 'center',
                fontSize: isMobileDevice() ? '14px' : '20px',
                color:
                  isGroupcalApp() && isToday(dateForHeader)
                    ? 'white'
                    : is24meApp() && isToday(dateForHeader)
                      ? 'black'
                      : textColor
              }}
            >
              {text}
            </div>
          </CircleContainer>
          <Typography fontSize={weekHeaderFontSize} color={dayNameTextColor}>
            {dayjs(dateForHeader)
              .tz(getDefaultTimezone())
              .format(
                props.dayNameFormat ?? (isMobileDevice() ? 'ddd' : 'dddd')
              )}
          </Typography>
        </Box>
      )
    case CalendarRange.DAY:
      return (
        <Styled.DayHeaderContentDay gap={2}>
          <CircleContainer
            color={
              isToday(dateForHeader)
                ? theme.palette.mode === 'dark'
                  ? theme.palette.primary.main
                  : APP_COLOR
                : undefined
            }
          >
            <Typography
              fontSize={'20px'}
              p={1}
              color={
                isGroupcalApp() && isToday(dateForHeader)
                  ? 'white'
                  : is24meApp() && isToday(dateForHeader)
                    ? 'black'
                    : textColor
              }
            >
              {dayjs(dateForHeader).format('D')}
            </Typography>
          </CircleContainer>

          <Typography fontSize={24} color={textColor}>
            {dayjs(dateForHeader).format(props.dayNameFormat ?? 'dddd')}
          </Typography>
        </Styled.DayHeaderContentDay>
      )
    default: {
      const now = dayjs()
      const header = dayjs(dateForHeader).utc()
      const visible = dayjs(
        Number(LocalStorage.get(LocalStorageKey.VISIBLE_DATE))
      )

      const isCurrentMonth =
        now.month() === visible.month() && now.year() === visible.year()
      const isSameDay = now.day() === header.day()
      return (
        <Typography
          marginTop={1}
          marginBottom={1}
          width={'100%'}
          fontSize={isMobileDevice() ? 14 : 18}
          color={
            isCurrentMonth && isSameDay
              ? theme.palette.mode === 'dark'
                ? 'var(--color-groupcal)'
                : APP_COLOR
              : theme.palette.text.primary
          }
        >
          {header.format(
            props.dayNameFormat ??
              (isMobileDevice() ||
              appMode.isCondensedHeight ||
              appMode.isCondensedWidth
                ? 'ddd'
                : 'dddd')
          )}
        </Typography>
      )
    }
  }
}

export function DayCellContent(props: DayCellInfo) {
  const bgColor = isToday(props.date) ? APP_COLOR : undefined
  switch (props.view.type) {
    case CALENDAR_VIEW[CalendarRange.WEEK]:
      return null
    case CALENDAR_VIEW[CalendarRange.DAY]:
      return null
    default:
      return (
        <div style={{ marginTop: 2, marginLeft: 1, marginRight: 1 }}>
          <CircleContainer color={bgColor} size={'24px'}>
            <Typography
              fontSize={'15px'}
              width={'100%'}
              minWidth={'24px'}
              lineHeight={0}
              textAlign={'center'}
              color={
                isToday(props.date) && isGroupcalApp()
                  ? 'white'
                  : isToday(props.date) && is24meApp()
                    ? 'black'
                    : undefined
              }
            >
              {props.dayNumberText}
            </Typography>
          </CircleContainer>
        </div>
      )
  }
}

interface EventContentProps extends EventDisplayInfo {
  closeAll?: boolean // for the drag/resize callbacks when we want to hide all opened popups
  ignoreDim?: boolean
  embedMode?: boolean
  eventModel?: EventDoc
  instanceStart?: number
  instanceEnd?: number
}

function EventContentInner(props: EventContentProps) {
  const eventModel =
    props.eventModel ??
    (props.event._def.extendedProps?.eventModel as EventDoc | undefined)

  let instanceStart = props.instanceStart
  let instanceEnd = props.instanceEnd
  if (instanceStart === undefined || instanceEnd === undefined) {
    const times = getEventInstanceTimes(
      props.event.startStr,
      props.event.endStr,
      props.event.allDay,
      eventModel
    )
    instanceStart = times.instanceStart
    instanceEnd = times.instanceEnd
  }

  return (
    <Event
      ignoreDim={props.ignoreDim}
      isStart={props.isStart}
      instanceStart={instanceStart}
      instanceEnd={instanceEnd}
      event={eventModel}
      title={props.event._def.title}
      closeAll={props.closeAll}
      fc_id={props.event._def.publicId}
      embedMode={props.embedMode}
      fcEvent={props.event}
    />
  )
}

// Do not memoize this component: Watermelon models may be mutated in place,
// which can make custom memo comparators incorrectly skip visual updates.
export const EventContent = EventContentInner

/**
 * Calculates the correct instance start and end timestamps for an event.
 * Handles the mismatch between all-day event formats in different calendar systems.
 *
 * @param startStr - FullCalendar start string
 * @param endStr - FullCalendar end string
 * @param allDay - Whether event is all-day
 * @param event - Event data (EventDoc or EventModelSnapshot)
 * @returns Object containing instanceStart and instanceEnd timestamps in milliseconds
 */
export function getEventInstanceTimes(
  startStr: string,
  endStr: string,
  allDay: boolean,
  event: EventDoc | EventModelSnapshot | undefined
): {
  instanceStart: number
  instanceEnd: number
} {
  let instanceStart = dayjs(startStr).valueOf()
  let instanceEnd = dayjs(endStr).valueOf()

  if (allDay && event) {
    instanceStart = dayjs.utc(startStr).startOf('day').valueOf()

    /**
     * because of mismatch between formats of all day events, we need to *remove* extra day for our data source, in order to show
     * events correctly on a screen. The reason for that is that for example event which goes from October 9 to October 10
     * will be saved in our system as "from 2024-10-09 to 2024-10-10", when FullCalendar and some other providers support
     * format as such: "from 2024-10-09 to 2024-10-11", meaning, last day of the long event is day after */

    instanceEnd = dayjs
      .utc(endStr.length > 0 ? endStr : startStr)
      .startOf('day')
      .subtract(
        event.StartDate !== event.EndDate ||
          (event.Recurrence && typeof event.Recurrence != 'string')
          ? 1
          : 0,
        'day'
      )
      .valueOf()

    /**
     * In case of all day event without a timezone (iOS bug) we do not need to subtract a day
     */
    if (event.TimeZoneNameID?.length === 0) {
      instanceEnd = dayjs
        .utc(endStr.length > 0 ? endStr : startStr)
        .startOf('day')
        .valueOf()
    }

    if (event.lateOriginalStartTime) {
      instanceStart = dayjs(
        Number(event.lateOriginalStartTime) * 1000
      ).valueOf()
    }

    if (event.lateOriginalEndTime) {
      instanceEnd = dayjs(Number(event.lateOriginalEndTime) * 1000).valueOf()
    }

    if (instanceEnd < instanceStart) {
      instanceEnd = instanceStart
    }
  }

  return { instanceStart, instanceEnd }
}

function AllDayContentView() {
  'use memo'

  return <Styled.SlotContent>{`${i18n.t('allDay')}`}</Styled.SlotContent>
}

function SlotLabelContentView(props: SlotHeaderInfo) {
  'use memo'

  const time = dayjs(props.date).tz(getDefaultTimezone())
  return (
    <Styled.SlotContent data-color="secondary" data-time>
      {time.hour() == 0 && time.minute() == 0
        ? ''
        : dateToTimeString(
            dayjs(props.date).tz(getDefaultTimezone()).toDate(),
            false,
            i18n.language.includes('ru')
          )}
    </Styled.SlotContent>
  )
}

export function AllDayContent() {
  'use no memo'

  return createElement(AllDayContentView)
}

export function SlotLabelContent(props: SlotHeaderInfo) {
  'use no memo'

  return createElement(SlotLabelContentView, props)
}

export default function dateToTimeString(
  dateObj: Date,
  keepMinutes?: boolean,
  newLineAMPM?: boolean,
  timezone?: string
): string {
  const date = dayjs(dateObj).tz(timezone || getDefaultTimezone())

  let formatString = 'h A'

  if (newLineAMPM) {
    formatString = 'h\nA'
  }

  if (
    (keepMinutes && date.get('minute') !== 0) ||
    (!date.format('A').toLowerCase().includes('am') &&
      !date.format('A').toLowerCase().includes('pm'))
  ) {
    formatString = 'h:mm A'

    if (newLineAMPM) {
      formatString = 'h:mm\nA'
    }
  }

  if (!isAMPM()) {
    formatString = 'HH:mm'
  } else {
    if (newLineAMPM) formatString = 'h:mm\nA'
    else formatString = 'h:mm A'
  }

  const timeString = date.format(formatString)

  return timeString.charAt(0) === '0' && timeString.length >= 5
    ? timeString.slice(1, timeString.length)
    : timeString
}
